import { FLOW_TYPE_RAG, FLOW_TYPE_API_SERVER } from './pipelineConstants';
/**
 * Flow metrics interval values (single source of truth).
 * Use these constants instead of string literals everywhere.
 */
export const FLOW_METRICS_INTERVAL_LIFETIME = 'lifetime';
export const FLOW_METRICS_INTERVAL_LATEST_RUN = 'latestRun';

export const FLOW_METRICS_INTERVALS = Object.freeze({
  LIFETIME: FLOW_METRICS_INTERVAL_LIFETIME,
  LATEST_RUN: FLOW_METRICS_INTERVAL_LATEST_RUN,
});

/**
 * Option set shown for batch / scheduled sources, in display order
 * (Latest Run first, then Lifetime) to match the long-standing toggle.
 */
export const BATCH_METRICS_INTERVALS = Object.freeze([
  FLOW_METRICS_INTERVAL_LATEST_RUN,
  FLOW_METRICS_INTERVAL_LIFETIME,
]);

/**
 * Windowed interval values for continuous-ingest sources (webhook / file upload / Kafka).
 * These resolve to a `{ from, to }` range at request time rather than a discrete run,
 * so the metrics endpoint is queried with `from`/`to` query params.
 *
 * `last24h` is a ROLLING window (now − 24h → now) sent as ISO instants. Because both
 * ends are absolute instants, the window is timezone-independent — it means the same
 * 24 hours for every user and needs no UTC-vs-local labeling. The monitoring backend
 * resolves sub-day ranges against its hourly buckets (retained ~7 days), so a rolling
 * 24h is always representable. This is the only window surfaced in the control today.
 *
 * `today` / `last7Days` / `last15Days` are calendar-day (UTC, date-only `YYYY-MM-DD`)
 * windows. They remain defined — `resolveWindowRange` still handles them — but are NOT
 * in the displayed option set: the per-node canvas control is a quick glance, and the
 * Flow Insights view already offers a full date-range picker for multi-day analysis.
 * Re-add one to `CONTINUOUS_METRICS_INTERVALS` to surface it.
 */
export const FLOW_METRICS_INTERVAL_LAST_24H = 'last24h';
export const FLOW_METRICS_INTERVAL_TODAY = 'today';
export const FLOW_METRICS_INTERVAL_LAST_7_DAYS = 'last7Days';
export const FLOW_METRICS_INTERVAL_LAST_15_DAYS = 'last15Days';
export const FLOW_METRICS_INTERVAL_CUSTOM = 'custom'; // reserved for the custom-range follow-up; not surfaced yet

/**
 * Interval values that resolve to a `{ from, to }` window via `resolveWindowRange`.
 * Drives `isWindowInterval` (the adapter's window-vs-run branch). Kept broader than the
 * displayed set so a window can be surfaced by adding it to `CONTINUOUS_METRICS_INTERVALS`
 * alone — `resolveWindowRange` and this list already support it.
 */
export const WINDOW_METRICS_INTERVALS = Object.freeze([
  FLOW_METRICS_INTERVAL_LAST_24H,
  FLOW_METRICS_INTERVAL_TODAY,
  FLOW_METRICS_INTERVAL_LAST_7_DAYS,
  FLOW_METRICS_INTERVAL_LAST_15_DAYS,
]);

/**
 * Option set shown for continuous-ingest sources, in display order.
 * Mirrors the batch toggle's shape: a single recent-window on the left, `Lifetime` on the
 * right — so the shared `Lifetime` anchor stays in the same position across both flow types
 * and the control does not visually shift when navigating batch ↔ continuous.
 * Lifetime stays the default, so there is no regression vs. the prior force-Lifetime behavior.
 */
export const CONTINUOUS_METRICS_INTERVALS = Object.freeze([
  FLOW_METRICS_INTERVAL_LAST_24H,
  FLOW_METRICS_INTERVAL_LIFETIME,
]);

export function isWindowInterval(interval) {
  return WINDOW_METRICS_INTERVALS.includes(interval);
}

/**
 * Flow types that should not display the metrics interval selector on the flow canvas.
 * These flow types either don't produce run-based metrics or handle them differently.
 */

export const METRICS_INTERVAL_EXCLUDED_FLOW_TYPES = Object.freeze([
  FLOW_TYPE_RAG,
  FLOW_TYPE_API_SERVER,
]);

export function isExcludedFlowType(flowType) {
  if (flowType == null) return false;
  return METRICS_INTERVAL_EXCLUDED_FLOW_TYPES.includes(flowType);
}

/**
 * Source types that receive data passively and don't have discrete runs.
 * These are windowed metrics sources: they support time-window intervals
 * (Today, Last 7 days, Last 15 days) instead of Latest Run.
 */
export const WINDOWED_SOURCE_TYPES = Object.freeze(['nexla_rest', 'file_upload']);

/**
 * Connection categories that are windowed metrics sources.
 * Kafka (and Kafka-family connectors like Confluent, PubSub, JMS)
 * are streaming sources that don't have discrete runs.
 */
export const WINDOWED_CONNECTION_CATEGORIES = Object.freeze(['kafka']);

/**
 * Returns true when the source context indicates a windowed (non-run-based) metrics source.
 * Both direct source types and connection-category-based vendors are supported.
 *
 * @param {{ sourceType?: string, connectionCategory?: string }} sourceContext
 */
export function isWindowedMetricsSource(sourceContext) {
  if (!sourceContext) return false;
  if (WINDOWED_SOURCE_TYPES.includes(sourceContext.sourceType)) return true;
  if (WINDOWED_CONNECTION_CATEGORIES.includes(sourceContext.connectionCategory)) return true;
  return false;
}

// Deprecated — kept for backward compatibility with `ec227d8` callers that still pass a bare sourceType.
// Migrate to `isWindowedMetricsSource({ sourceType })` when you touch the call site.
export const METRICS_INTERVAL_EXCLUDED_SOURCE_TYPES = WINDOWED_SOURCE_TYPES;

export function isExcludedSourceType(sourceType) {
  if (sourceType == null) return false;
  return WINDOWED_SOURCE_TYPES.includes(sourceType);
}

/**
 * Valid interval option set for a given source context, in display order.
 * Windowed sources (webhook, file upload, Kafka) use the windowed set; everything else uses the batch set.
 * Drives both the rendered selector options and `resolveEffectiveInterval`'s validity check.
 *
 * @param {{ sourceType?: string, connectionCategory?: string }} sourceContext
 */
export function getMetricsIntervalOptions(sourceContext) {
  return isWindowedMetricsSource(sourceContext)
    ? CONTINUOUS_METRICS_INTERVALS
    : BATCH_METRICS_INTERVALS;
}

/**
 * Default interval for a source context — the most-recent meaningful view for that source class.
 * Symmetric across flow types: batch defaults to `latestRun`, windowed (continuous) defaults to
 * `last24h`. `Lifetime` is never a forced default — it is the deliberate all-time opt-in, picked
 * automatically only if a source ever had no recent-view option at all.
 *
 * (This is a deliberate change from the original "windowed → Lifetime (no-regression)" default:
 * showing all-time by default on an always-on source is the least useful number and is
 * inconsistent with batch, which already defaults to its recent view. A quiet flow correctly
 * shows a real 0 for the last 24h; Lifetime is one click away.)
 *
 * @param {{ sourceType?: string, connectionCategory?: string }} sourceContext
 */
export function getDefaultMetricsInterval(sourceContext) {
  return isWindowedMetricsSource(sourceContext)
    ? FLOW_METRICS_INTERVAL_LAST_24H
    : FLOW_METRICS_INTERVAL_LATEST_RUN;
}

/**
 * Returns `interval` if it is valid for the source's option set, otherwise the source's default.
 * Prevents a stored value from one selector (e.g. batch-only `latestRun`) from leaking into a
 * source whose selector does not offer it.
 *
 * @param {{ sourceType?: string, connectionCategory?: string }} sourceContext
 * @param {string} interval
 */
export function resolveEffectiveInterval(sourceContext, interval) {
  return getMetricsIntervalOptions(sourceContext).includes(interval)
    ? interval
    : getDefaultMetricsInterval(sourceContext);
}
